import type { EditorHost } from '@blocksuite/affine/block-std';
import { type AIError, openFileOrFiles } from '@blocksuite/affine/blocks';
import { assertExists, WithDisposable } from '@blocksuite/affine/global/utils';
import { css, html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  ChatAbortIcon,
  ChatClearIcon,
  ChatSendIcon,
  CloseIcon,
  ImageIcon,
  MoreIcon
} from '../_common/icons';
import { AIProvider } from '../provider';
import { reportResponse } from '../utils/action-reporter';
import { readBlobAsURL } from '../utils/image';
import type { ChatContextValue, ChatMessage } from './chat-context';

const MaximumImageCount = 32;

function getFirstTwoLines(text: string) {
  const lines = text.split('\n');
  return lines.slice(0, 2);
}

export class ChatPanelInput extends WithDisposable(LitElement) {
  static override styles = css`
    .chat-panel-btn {
      border: 1px solid #0000001a;
      background: white;
      padding: 5px 10px;
      cursor: pointer;
      border-radius: 5px;
      transition: .2s;

      &:hover {
        background: #f8f8f8;
      }

      &:active {
        background: #eeeeee;
      }
    }

    .chat-panel-input {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 12px;
      position: relative;
      margin-top: 5px;
      border-radius: 4px;
      padding: 8px;
      min-height: 94px;
      box-sizing: border-box;
      border-width: 1px;
      border-style: solid;

      .chat-selection-quote {
        padding: 4px 0px 8px 0px;
        padding-left: 15px;
        max-height: 56px;
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        color: var(--affine-text-secondary-color);
        position: relative;

        div {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chat-quote-close {
          position: absolute;
          right: 0;
          top: 0;
          cursor: pointer;
          display: none;
          width: 16px;
          height: 16px;
          border-radius: 4px;
          border: 1px solid var(--affine-border-color);
          background-color: var(--affine-white);
        }
      }

      .chat-selection-quote:hover .chat-quote-close {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .chat-selection-quote::after {
        content: '';
        width: 2px;
        height: calc(100% - 10px);
        margin-top: 5px;
        position: absolute;
        left: 0;
        top: 0;
        background: var(--affine-quote-color);
        border-radius: 18px;
      }
    }

    .chat-panel-input-actions {
      display: flex;
      gap: 8px;
      align-items: center;

      div {
        width: 24px;
        height: 24px;
        cursor: pointer;
      }

      div:nth-child(2) {
        margin-left: auto;
      }

      .image-upload {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .chat-panel-input {
      textarea {
        width: 100%;
        padding: 0;
        margin: 0;
        border: none;
        line-height: 22px;
        font-size: var(--affine-font-sm);
        font-weight: 400;
        font-family: var(--affine-font-family);
        color: var(--affine-text-primary-color);
        box-sizing: border-box;
        resize: none;
        overflow-y: hidden;
      }

      textarea::placeholder {
        font-size: 14px;
        font-weight: 400;
        font-family: var(--affine-font-family);
        color: var(--affine-placeholder-color);
      }

      textarea:focus {
        outline: none;
      }
    }

    .chat-panel-images-wrapper {
      overflow: hidden scroll;
      max-height: 128px;

      .chat-panel-images {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        position: relative;

        .video-container {
          width: 150px;
          height: 100px;
          cursor: pointer;
          overflow: hidden;
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;

          video {
            width: 100%;
            height: 100%;
          }
        }
        .image-container {
          width: 58px;
          height: 58px;
          border-radius: 4px;
          border: 1px solid var(--affine-border-color);
          cursor: pointer;
          overflow: hidden;
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;

          img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
          }
        }
      }
    }

    .chat-panel-send svg rect {
      fill: var(--affine-primary-color);
    }

    .chat-panel-send[aria-disabled='true'] {
      cursor: not-allowed;
    }

    .chat-panel-send[aria-disabled='true'] svg rect {
      fill: var(--affine-text-disable-color);
    }

    .close-wrapper {
      width: 16px;
      height: 16px;
      border-radius: 4px;
      border: 1px solid var(--affine-border-color);
      justify-content: center;
      align-items: center;
      display: none;
      position: absolute;
      background-color: var(--affine-white);
      z-index: 1;
      cursor: pointer;
    }

    .close-wrapper:hover {
      background-color: var(--affine-background-error-color);
      border: 1px solid var(--affine-error-color);
    }

    .close-wrapper:hover svg path {
      fill: var(--affine-error-color);
    }
  `;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @query('.chat-panel-images')
  accessor imagesWrapper!: HTMLDivElement;

  @query('textarea')
  accessor textarea!: HTMLTextAreaElement;

  @query('.close-wrapper')
  accessor closeWrapper!: HTMLDivElement;

  @state()
  accessor curIndex = -1;

  @state()
  accessor isInputEmpty = true;

  @state()
  accessor focused = false;

  @property({ attribute: false })
  accessor chatContextValue!: ChatContextValue;

  @property({ attribute: false })
  accessor updateContext!: (context: Partial<ChatContextValue>) => void;

  @property({ attribute: false })
  accessor cleanupHistories!: () => Promise<void>;

  private _addImages(images: File[]) {
    const oldImages = this.chatContextValue.images;
    this.updateContext({
      images: [...oldImages, ...images].slice(0, MaximumImageCount),
    });
  }

  private _renderImageItem (image: File, index: number) {
    return html`
      <div
        class="image-container"
        @mouseenter=${(evt: MouseEvent) => {
          const ele = evt.target as HTMLImageElement;
          const rect = ele.getBoundingClientRect();
          assertExists(ele.parentElement?.parentElement);
          const parentRect =
            ele.parentElement.parentElement.getBoundingClientRect();
          const left = Math.abs(rect.right - parentRect.left) - 8;
          const top = Math.abs(parentRect.top - rect.top);
          this.curIndex = index;
          this.closeWrapper.style.opacity = '1';
          this.closeWrapper.style.pointerEvents = 'auto';
          this.closeWrapper.style.left = left + 'px';
          this.closeWrapper.style.top = top + 'px';
        }}
      >
        <img src="${URL.createObjectURL(image)}" alt="${image.name}" />
      </div>`
  }

  private _renderVideoItem (video: File, index: number) {
    return html`
      <div
        class="video-container"
        @mouseenter=${(evt: MouseEvent) => {
          const ele = evt.target as HTMLImageElement;
          const rect = ele.getBoundingClientRect();
          assertExists(ele.parentElement?.parentElement);
          const parentRect =
            ele.parentElement.parentElement.getBoundingClientRect();
          const left = Math.abs(rect.right - parentRect.left) - 8;
          const top = Math.abs(parentRect.top - rect.top);
          this.curIndex = index;
          this.closeWrapper.style.opacity = '1';
          this.closeWrapper.style.pointerEvents = 'auto';
          this.closeWrapper.style.left = left + 'px';
          this.closeWrapper.style.top = top + 'px';
        }}
      >
        <video
          style="width: 100%;height: 100%;"
          title="视频:${index+1}"
          src="${URL.createObjectURL(video)}"
          controls
        />
      </div>`
  }

  private _renderImages(images: File[]) {
    return html`
      <div
        class="chat-panel-images-wrapper"
        @mouseleave=${() => {
          this.closeWrapper.style.opacity = '0';
          this.closeWrapper.style.pointerEvents = 'none';
          this.curIndex = -1;
        }}
      >
        <div class="chat-panel-images">
          ${repeat(
            images,
            image => image.name,
            (file, index) => {
              if (['image/png', 'image/jpeg'].includes(file.type)) return this._renderImageItem(file, index)
              if ('video/mp4' === file.type || file.type === '') return this._renderVideoItem(file, index)
              return this._renderImageItem(file, index)
            }
          )}
        </div>
        <div
          class="close-wrapper"
          @click=${() => {
            AIProvider.slots.toggleChatCards.emit({ visible: true });

            if (this.curIndex >= 0 && this.curIndex < images.length) {
              const newImages = [...images];
              newImages.splice(this.curIndex, 1);
              this.updateContext({ images: newImages });
              this.curIndex = -1;
              this.closeWrapper.style.display = 'none';
            }
          }}
        >
          ${CloseIcon}
        </div>
      </div>
    `;
  }

  override connectedCallback() {
    super.connectedCallback();

    this._disposables.add(
      AIProvider.slots.requestSendWithChat.on(async ({ input, context }) => {
        context && this.updateContext(context);
        await this.updateComplete;
        await this.send(input);
      })
    );
  }

  protected override render() {
    const { images: files, status } = this.chatContextValue;
    const hasFiles = files.length > 0
    const maxHeight = hasFiles ? 272 + 2 : 200 + 2;
    const getPrevDocIndex = () => {
      return this.chatContextValue.items?.findLastIndex(r => r.role === 'user' && r.content?.indexOf('引用下面内容的观点和数据，融合成一篇新的文档，如果内容提供了大纲或者标题，要引用内容中提供的大纲或标题：') !== -1)
    }
    const getPrevAiIndex = () => {
      const prevDocIndex = getPrevDocIndex()
      return prevDocIndex === -1 ? -1 : prevDocIndex + 1;
    }
    function extractDocuments(input) {
      // 匹配格式：第X篇文档，文档标题：标题。文档内容：内容，以上是第X篇文档；
      const regex = /融合文档第(\d+)篇，文档标题：(.*?)，文档内容：(.*?)，提交人：(.*?)，以上是第\1篇文档；/gs;
      const documents = [];
      let match;

      while ((match = regex.exec(input)) !== null) {
        documents.push({
          index: parseInt(match[1]),
          title: match[2],
          content: match[3],
          userName: match[4],
        });
      }

      return documents;
    }

    // 获取贡献度
    const getContributions = () => {
      const aiItems = this.chatContextValue.items;
      // 获取融合文档内容
      const prevDocIndex = getPrevDocIndex()
      const docResources = aiItems?.[prevDocIndex]
      const extractResult = extractDocuments(docResources.content)
      // 获取融合结果
      const prevAiIndex = getPrevAiIndex()
      const aiResources = aiItems?.[prevAiIndex]

      fetch('/api/auth/compute_contributions', {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          fused_text: aiResources.content,
          contributions: extractResult.reduce((acc, item) => {
            const key = `${item.title}(${item.userName})`
            acc[key] = item.content
            return acc
          }, {})
        })
      }).then(res => res.json())
        .then(res => {
        const data = res?.contribution_percentages ?? {}
        const assistantText = Object.entries(data).reduce((acc, item) => {
          acc += `${item[0]}：${item[1]}；`
          return acc
        }, '')
        this.updateContext({
          items: [
            ...this.chatContextValue.items,
            {
              id: '',
              role: 'assistant',
              content: `融合得分：${assistantText}`,
              createdAt: new Date().toISOString(),
            },
          ],
        });
      })
    }
    return html`<style>
        .chat-panel-input {
          border-color: ${this.focused
            ? 'var(--affine-primary-color)'
            : 'var(--affine-border-color)'};
          box-shadow: ${this.focused ? 'var(--affine-active-shadow)' : 'none'};
          max-height: ${maxHeight}px !important;
          user-select: none;
        }
      </style>
      <div style="margin-top: 10px">
        ${
          (() => {
            const isPrevDocIndex = getPrevDocIndex()
            const prevAiIndex = getPrevAiIndex()
            return isPrevDocIndex !== -1 && prevAiIndex !== -1 && this.chatContextValue.items?.[prevAiIndex]?.content
          })() ?
            html`
              <button class="chat-panel-btn" @click=${getContributions}>计算贡献度</button>
            `: undefined
        }
      </div>
      <div class="chat-panel-input">
        ${hasFiles ? this._renderImages(files) : nothing}
        ${this.chatContextValue.quote
          ? html`<div class="chat-selection-quote">
              ${repeat(
                getFirstTwoLines(this.chatContextValue.quote),
                line => line,
                line => html`<div>${line}</div>`
              )}
              <div
                class="chat-quote-close"
                @click=${() => {
                  AIProvider.slots.toggleChatCards.emit({ visible: true });
                  this.updateContext({ quote: '', markdown: '' });
                }}
              >
                ${CloseIcon}
              </div>
            </div>`
          : nothing}
        <textarea
          rows="1"
          placeholder="需要什么帮助?"
          @input=${() => {
            const { textarea } = this;
            this.isInputEmpty = !textarea.value.trim();
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
            let imagesHeight = this.imagesWrapper?.scrollHeight ?? 0;
            if (imagesHeight) imagesHeight += 12;
            if (this.scrollHeight >= 200 + imagesHeight) {
              textarea.style.height = '148px';
              textarea.style.overflowY = 'scroll';
            }
          }}
          @keydown=${async (evt: KeyboardEvent) => {
            if (evt.key === 'Enter' && !evt.shiftKey && !evt.isComposing) {
              evt.preventDefault();
              await this.send();
            }
          }}
          @focus=${() => {
            this.focused = true;
          }}
          @blur=${() => {
            this.focused = false;
          }}
          @paste=${(event: ClipboardEvent) => {
            const items = event.clipboardData?.items;
            if (!items) return;

            for (const index in items) {
              const item = items[index];
              if (item.kind === 'file' && item.type.indexOf('image') >= 0) {
                const blob = item.getAsFile();
                if (!blob) continue;
                this._addImages([blob]);
              }
            }
          }}
          data-testid="chat-panel-input"
        ></textarea>
        <div class="chat-panel-input-actions">
          <div
            class="chat-history-clear"
            @click=${async () => {
              await this.cleanupHistories();
            }}
            data-testid="chat-panel-clear"
          >
            ${ChatClearIcon}
          </div>
          ${files.length < MaximumImageCount
            ? html`
              <div
                class="image-upload"
                @click=${async () => {
                  const images = await openFileOrFiles({
                    acceptType: 'Videos',
                    multiple: true,
                  });
                  if (!images) return;
                  this._addImages(images);
                }}
              >
                ${MoreIcon}
              </div>
            `  : nothing
          }

          ${files.length < MaximumImageCount
            ? html`<div
                class="image-upload"
                @click=${async () => {
                  const images = await openFileOrFiles({
                    acceptType: 'Images',
                    multiple: true,
                  });
                  if (!images) return;
                  this._addImages(images);
                }}
              >
                ${ImageIcon}
              </div>`
            : nothing}
          ${status === 'transmitting'
            ? html`<div
                @click=${() => {
                  this.chatContextValue.abortController?.abort();
                  this.updateContext({ status: 'success' });
                  reportResponse('aborted:stop');
                }}
              >
                ${ChatAbortIcon}
              </div>`
            : html`<div
                @click="${() => this.send()}"
                class="chat-panel-send"
                aria-disabled=${this.isInputEmpty}
                data-testid="chat-panel-send"
              >
                ${ChatSendIcon}
              </div>`}
        </div>
      </div>`;
  }

  send = async (input?: string) => {
    const { status, markdown } = this.chatContextValue;
    if (status === 'loading' || status === 'transmitting') return;

    const text = input || this.textarea.value;
    const { images } = this.chatContextValue;
    if (!text && images.length === 0) {
      return;
    }
    const { doc } = this.host;
    this.textarea.value = '';
    this.isInputEmpty = true;
    this.textarea.style.height = 'unset';
    this.updateContext({
      images: [],
      status: 'loading',
      error: null,
      quote: '',
      markdown: '',
    });

    const attachments = await Promise.all(
      images?.map(image => readBlobAsURL(image))
    );

    const content = (markdown ? `${markdown}\n` : '') + text;

    this.updateContext({
      items: [
        ...this.chatContextValue.items,
        {
          id: '',
          role: 'user',
          content: content,
          createdAt: new Date().toISOString(),
          attachments,
        },
        {
          id: '',
          role: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
        },
      ],
    });

    try {
      const abortController = new AbortController();
      const stream = AIProvider.actions.chat?.({
        input: content,
        docId: doc.id,
        attachments: images,
        workspaceId: doc.collection.id,
        host: this.host,
        stream: true,
        signal: abortController.signal,
        where: 'chat-panel',
        control: 'chat-send',
        isRootSession: true,
      });

      if (stream) {
        this.updateContext({ abortController });

        for await (const text of stream) {
          const items = [...this.chatContextValue.items];
          const last = items[items.length - 1] as ChatMessage;
          last.content += text;
          this.updateContext({ items, status: 'transmitting' });
        }

        this.updateContext({ status: 'success' });

        if (!this.chatContextValue.chatSessionId) {
          this.updateContext({
            chatSessionId: AIProvider.LAST_ROOT_SESSION_ID,
          });
        }

        const { items } = this.chatContextValue;
        const last = items[items.length - 1] as ChatMessage;
        if (!last.id) {
          const historyIds = await AIProvider.histories?.ids(
            doc.collection.id,
            doc.id,
            { sessionId: this.chatContextValue.chatSessionId }
          );
          if (!historyIds || !historyIds[0]) return;
          last.id = historyIds[0].messages.at(-1)?.id ?? '';
        }
      }
    } catch (error) {
      this.updateContext({ status: 'error', error: error as AIError });
    } finally {
      this.updateContext({ abortController: null });
    }
  };

  constructor(props) {
    super(props);
    window.send = this.send
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-panel-input': ChatPanelInput;
  }
}
